// Core
import { Auth } from '@aws-amplify/auth';

// Store
import { store } from '../store/index';
import { logUserIn, logUserOut } from '../store/auth/actions';

// Plugins
import { requestVantageApi } from './request';

/**
 * Log the user out from the application
 * @param history {History} The React Router history to apply redirecting
 * @param t The internationalization tool to translate messages
 * @param logUserOut {dispatch func} redux dispatch action to log user out
 * @param redirect {boolean} If the user must be redirected to login
 * @param showError {boolean} If the error message must be displayed
 */
export const performLogout = async (redirect = true) => {
  await Auth.signOut();
  store.dispatch(logUserOut());
  localStorage.removeItem('lastUserInteraction');
  if (redirect) window.location.href = '/';
};

/**
 * Keep retrying to log user out if any failure
 * @param history {History} The React Router history to apply redirecting
 * @param t The internationalization tool to translate messages
 * @param logUserOut {dispatch func} redux dispatch action to log user out
 */
export const retryAutomaticLogout = async () => {
  performLogout(true)
    .catch(() => {
      setTimeout(() => {
        retryAutomaticLogout();
      }, 60000); // one minute
    });
};

/**
 * Performs the login request to AWS Cognito
 * @param userEmail {string} User email
 * @param userPassword {string} User password
 */
export const performLogin = async (userEmail, userPassword) => {
  const loginResult = {
    success: false,
    cognitoResponse: null,
    newPasswordRequired: false,
  };

  try {
    loginResult.cognitoResponse = await Auth.signIn(userEmail, userPassword);

    if (loginResult.cognitoResponse.challengeName === 'NEW_PASSWORD_REQUIRED') {
      // New users have to set new password
      loginResult.newPasswordRequired = true;
    } else {
      // Request user's details
      const apiUserResponse = await requestVantageApi.user('get', `/${loginResult.cognitoResponse.username}`);

      // store user's data
      await store.dispatch(logUserIn({ user: apiUserResponse.data }));
      loginResult.success = true;
    }
  } catch {
    await performLogout(false);
  }

  return loginResult;
};

export const setNewPassword = async (cognitoUserObject, email, newPassword) => {
  let success = false;

  try {
    await Auth.completeNewPassword(cognitoUserObject, newPassword);

    // perform user login with new password
    await performLogin(email, newPassword);
    success = true;
  } catch {
    await performLogout(false);
  }

  return success;
};
